/**
 * Settings Page for Admin PWA
 */

import React, { useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { pwaPath } from '@/utils/getBasePath';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Building2, Clock, CreditCard, Bell, Shield, Palette,
  ChevronRight, KeyRound
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import POSPinSetup from '../components/POSPinSetup';

// Only sections with a working screen are listed.
const settingSections = [
  { id: 'payments', name: 'Payments', icon: CreditCard, description: 'Payment methods and processing', href: '../payment-config' },
  { id: 'pos-pins', name: 'POS PIN Setup', icon: KeyRound, description: 'Assign POS login PINs to employees' },
];

export default function SettingsPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  if (section && section !== 'pos-pins') {
    return <Navigate to={section === 'payments' ? '../payment-config' : '../settings'} replace />;
  }

  if (section) {
    const sectionInfo = settingSections.find(s => s.id === section);

    return (
      <div className="space-y-4 sm:space-y-6">
        <div>
          <Button variant="ghost" onClick={() => navigate(pwaPath('admin', 'settings'))} className="text-slate-400 mb-2 h-8 text-sm">
            &larr; Back
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold text-white">
            {sectionInfo?.name || section}
          </h1>
        </div>

        <POSPinSetup token={token} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-white">Settings</h1>
        <p className="text-slate-400 text-sm mt-0.5 sm:mt-1">
          Configure your restaurant
        </p>
      </div>

      <div className="space-y-2">
        {settingSections.map(s => (
          <Card 
            key={s.id}
            className="bg-slate-900 border-slate-800 hover:border-slate-700 cursor-pointer transition-colors"
            onClick={() => navigate(s.href || pwaPath('admin', `settings/${s.id}`))}
            data-testid={`settings-section-${s.id}`}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="p-2 sm:p-2.5 bg-slate-800 rounded-lg flex-shrink-0">
                  <s.icon className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-white text-sm sm:text-base">{s.name}</h3>
                  <p className="text-xs sm:text-sm text-slate-500 truncate">{s.description}</p>
                </div>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
